import { useCallback, useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../utils/errorLogging';

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/jpeg',
  'image/png',
];

export const useDocuments = ({ projectId, recordId, recordType = 'financial' }) => {
  const supabase = useSupabaseClient();
  const { logError } = useErrorLogging();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  // Validate file before upload
  const validateFile = useCallback(file => {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`הקובץ ${file.name} גדול מדי. הגודל המקסימלי הוא 25MB`);
    }
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      throw new Error(`סוג הקובץ ${file.type} אינו נתמך`);
    }
  }, []);

  // Upload file to Supabase Storage
  const uploadToStorage = useCallback(
    async (file, filePath) => {
      const bucketName = 'financial-documents';
      const { data, error } = await supabase.storage.from(bucketName).upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        onUploadProgress: progress => {
          setUploadProgress((progress.loaded / progress.total) * 100);
        },
      });

      if (error) {
        logError(error, {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.UI,
          message: 'Failed to upload document',
          context: { error, filePath, projectId, recordId, recordType },
        });
        throw error;
      }
      return data;
    },
    [supabase],
  );

  // Main upload handler
  const uploadDocument = useCallback(
    async file => {
      setIsUploading(true);
      setUploadProgress(0);
      setError(null);

      try {
        // Validate file
        validateFile(file);

        // Generate unique file path
        const timestamp = new Date().getTime();
        const filePath = `${projectId}/${recordType}/${recordId}/${timestamp}_${file.name}`;

        // Upload to storage

        await uploadToStorage(file, filePath);
        const {
          data: { signedUrl },
        } = await supabase.storage
          .from('financial-documents')
          .createSignedUrl(filePath, 60 * 60 * 24 * 7); // 7 days

        // Call RPC to update database

        const { data: rpcData, error: rpcError } = await supabase.rpc('handle_document_upload', {
          p_project_id: projectId,
          p_record_id: recordId,
          p_record_type: recordType,
          p_file_path: filePath,
          p_file_name: file.name,
          p_file_url: signedUrl,
        });

        if (rpcError) {
          throw rpcError;
        }

        return rpcData;
      } catch (err) {
        setError(err.message);
        logError(err, {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.UPLOAD,
          message: 'Failed to upload document',
          context: {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            recordType,
            recordId,
          },
        });
        throw err;
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    },
    [projectId, recordId, recordType, supabase, validateFile, uploadToStorage, logError],
  );

  // Soft delete document by marking it as deleted in the database
  const deleteDocument = useCallback(
    async path => {
      try {
        // Ensure path is relative to bucket (remove leading slash if present)
        const relativePath = path.replace(/^\/+/, '');

        // Call RPC to mark document as deleted
        const { error: rpcError } = await supabase.rpc('handle_document_delete', {
          p_project_id: projectId,
          p_record_id: recordId,
          p_record_type: recordType,
          p_file_path: relativePath,
        });

        if (rpcError) {
          throw rpcError;
        }

        return true;
      } catch (err) {
        logError(err, {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.DELETE,
          message: 'Failed to delete document',
          context: { path, recordId, recordType, projectId },
        });
        throw err;
      }
    },
    [supabase, logError, projectId, recordId, recordType],
  );

  // Filter out deleted documents
  const filterDocuments = useCallback(documents => {
    if (!documents) return [];
    return documents.filter(doc => !doc.deleted_at);
  }, []);

  return {
    uploadDocument,
    deleteDocument,
    filterDocuments,
    isUploading,
    uploadProgress,
    error,
  };
};
